/**
 * Indisponibilità: periodi con date in cui una persona non è operativa (ferie, fuori sede, mezza giornata).
 * Regole pure, test in __tests__/unavailability.test.ts.
 *
 * Vivono nella tabella `calendar_unavailability`. Le pause che si ripetono ogni settimana restano in
 * `settings.calendar_settings.availability` (workHours.ts): qui si trattano allo stesso modo, come "occupato"
 * nella vista Team e nell'avviso del modulo, e in piu' compaiono nelle viste del calendario.
 */
import type { CalendarEvent } from '../../types';
import { atLeast, type ShareLevel } from '../sharing';
import { clampToDay } from './availability';
import type { DayBlock } from './workHours';

export interface Unavailability {
  id: string;
  organization_id: string;
  user_id: string;
  title: string;
  note: string | null;
  start_time: string;
  end_time: string;
  all_day: boolean;
  created_by: string | null;
}

export const UNAVAILABLE_TITLE = 'Non disponibile';
export const UNAVAILABILITY_EVENT_TYPE = 'unavailability';
const PREFIX = 'unavailability_';

/** Chi può segnare un'indisponibilità sul calendario di una persona: lei stessa, o chi ha il permesso. */
export function canManageUnavailability(personId: string, meId: string | null | undefined, canScheduleOthers: boolean): boolean {
  return !!meId && (personId === meId || canScheduleOthers);
}

/** Le indisponibilità della persona in quel giorno, come blocchi in minuti (le giornate intere coprono tutto il giorno). */
export function unavailabilityBlocksFor(day: Date, rows: readonly Unavailability[], personId: string | null | undefined): DayBlock[] {
  if (!personId) return [];
  const out: DayBlock[] = [];
  for (const r of rows) {
    if (r.user_id !== personId) continue;
    const c = clampToDay(r, day);
    if (c) out.push({ id: `${PREFIX}${r.id}`, title: r.title || UNAVAILABLE_TITLE, ...c });
  }
  return out.sort((a, b) => a.start - b.start);
}

/** L'id della riga da un blocco o da un evento del calendario; null se non è un'indisponibilità. */
export function unavailabilityIdOf(id: string | null | undefined): string | null {
  return id && id.startsWith(PREFIX) ? id.slice(PREFIX.length) : null;
}

/**
 * Le indisponibilità come eventi del calendario, per i calendari accesi (e sempre quelle segnate da me). Grigie a righe come "solo occupato",
 * non trascinabili; il motivo si vede da "Vede i dettagli" in su, altrimenti "Non disponibile".
 */
export function unavailabilityEvents(
  rows: readonly Unavailability[],
  ctx: { meId: string; visible: Record<string, ShareLevel>; shown: Record<string, true>; nameOf: (id: string) => string | undefined },
): CalendarEvent[] {
  const out: CalendarEvent[] = [];
  for (const r of rows) {
    const mine = r.created_by === ctx.meId;
    // Quelle che ho segnato io le vedo sempre (come le task commerciali che fisso ai colleghi)
    const level = r.user_id === ctx.meId || mine ? 'edit' : ctx.visible[r.user_id];
    if (!level || (!ctx.shown[r.user_id] && !mine)) continue;
    const reason = atLeast(level, 'details') ? r.title || UNAVAILABLE_TITLE : UNAVAILABLE_TITLE;
    const name = r.user_id === ctx.meId ? null : ctx.nameOf(r.user_id);
    out.push({
      id: `${PREFIX}${r.id}`,
      title: name ? `${name}: ${reason}` : reason,
      description: atLeast(level, 'details') ? r.note || '' : '',
      start_time: r.start_time,
      end_time: r.end_time,
      all_day: r.all_day,
      created_at: '',
      color: '#9CA3AF',
      is_editable: false,
      event_type: UNAVAILABILITY_EVENT_TYPE,
      metadata: { owner_id: r.user_id, busy_only: true, unavailability_id: r.id } as any,
    });
  }
  return out;
}

/** AAAA-MM-GG + HH:MM in ora locale. */
function localDate(date: string, time = '00:00'): Date {
  return new Date(`${date}T${time}:00`);
}

/**
 * Il periodo da salvare a partire dal modulo. Giornate intere: dalla mezzanotte del primo giorno alla mezzanotte
 * dopo l'ultimo. Restituisce il motivo dell'errore se il periodo non va bene.
 */
export function periodFromForm(f: { allDay: boolean; fromDate: string; fromTime: string; toDate: string; toTime: string }):
  { start_time: string; end_time: string; all_day: boolean } | { error: string } {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(f.fromDate) || !/^\d{4}-\d{2}-\d{2}$/.test(f.toDate)) return { error: 'Scegli le date' };
  const start = f.allDay ? localDate(f.fromDate) : localDate(f.fromDate, f.fromTime);
  const end = f.allDay ? localDate(f.toDate) : localDate(f.toDate, f.toTime);
  if (f.allDay) end.setDate(end.getDate() + 1);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return { error: 'Controlla date e orari' };
  if (end <= start) return { error: f.allDay ? "L'ultimo giorno viene prima del primo" : 'La fine viene prima dell\'inizio' };
  return { start_time: start.toISOString(), end_time: end.toISOString(), all_day: f.allDay };
}

function pad(n: number) { return String(n).padStart(2, '0'); }
function dateKey(d: Date) { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }
function timeKey(d: Date) { return `${pad(d.getHours())}:${pad(d.getMinutes())}`; }

/** Il contrario di periodFromForm: i campi del modulo da una riga salvata (l'ultimo giorno pieno compreso). */
export function formFromPeriod(r: Pick<Unavailability, 'start_time' | 'end_time' | 'all_day'>) {
  const start = new Date(r.start_time);
  const end = new Date(r.end_time);
  if (r.all_day) {
    const last = new Date(end);
    last.setDate(last.getDate() - 1);
    return { allDay: true, fromDate: dateKey(start), fromTime: '09:00', toDate: dateKey(last), toTime: '18:00' };
  }
  return { allDay: false, fromDate: dateKey(start), fromTime: timeKey(start), toDate: dateKey(end), toTime: timeKey(end) };
}

/** I campi del modulo per un periodo nuovo: un orario scelto sul calendario, altrimenti la giornata di oggi. */
export function formForNew(start?: Date | null, end?: Date | null, allDay?: boolean) {
  const s = start ?? new Date();
  if (allDay || !start) {
    // La fine di una selezione di giornate e' la mezzanotte dopo l'ultimo giorno; una fine non dopo l'inizio vale un giorno solo
    const last = end && end > s ? new Date(end.getTime() - 1) : s;
    return { allDay: true, fromDate: dateKey(s), fromTime: '09:00', toDate: dateKey(last), toTime: '18:00' };
  }
  const e = end ?? new Date(s.getTime() + 60 * 60000);
  return { allDay: false, fromDate: dateKey(s), fromTime: timeKey(s), toDate: dateKey(e), toTime: timeKey(e) };
}
